import { useState } from 'react';
import { Icon } from '../../icons';

export default function MetricCard({ label, value, delta, deltaUp, icon, accent, loading = false, onClick, sublabel, className = "" }) {
  const cfg = {
    blue:   { grad: "linear-gradient(135deg,#4f46e5,#818cf8)", soft: "rgba(79,70,229,0.08)",  glow: "rgba(79,70,229,0.22)"  },
    green:  { grad: "linear-gradient(135deg,#059669,#34d399)", soft: "rgba(16,185,129,0.08)", glow: "rgba(16,185,129,0.22)" },
    amber:  { grad: "linear-gradient(135deg,#d97706,#fbbf24)", soft: "rgba(245,158,11,0.08)", glow: "rgba(245,158,11,0.22)" },
    red:    { grad: "linear-gradient(135deg,#dc2626,#f87171)", soft: "rgba(239,68,68,0.08)",  glow: "rgba(239,68,68,0.22)"  },
    purple: { grad: "linear-gradient(135deg,#7c3aed,#a78bfa)", soft: "rgba(139,92,246,0.08)", glow: "rgba(139,92,246,0.22)" },
    cyan:   { grad: "linear-gradient(135deg,#0284c7,#38bdf8)", soft: "rgba(6,182,212,0.08)",  glow: "rgba(6,182,212,0.22)"  },
  };
  const c = cfg[accent] || cfg.blue;
  const [hovered, setHovered] = useState(false);
  const valStr = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  const valLen = valStr.length;
  const mobileFontSize = valLen >= 10
    ? 13
    : valLen >= 8
    ? 14
    : valLen >= 6
    ? 15
    : valLen >= 5
    ? 16
    : 18;

  const desktopClass = valLen >= 12
    ? 'sm:!text-[16px]'
    : valLen >= 9
    ? 'sm:!text-[18px]'
    : valLen >= 6
    ? 'sm:!text-[20px]'
    : 'sm:!text-[22px]';

  return (
    <div
      onClick={onClick}
      className={`bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800/50 flex flex-col justify-between ${className}`}
      style={{
        borderRadius: 16,
        boxShadow: hovered && onClick ? `0 10px 30px ${c.glow}` : "0 1px 3px rgba(0,0,0,0.04),0 4px 20px rgba(0,0,0,0.04)",
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "all .2s ease",
        transform: hovered && onClick ? "translateY(-2px)" : "none"
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ height: 4, background: c.grad }} />
      <div className="p-2.5 sm:p-4 flex-1 flex flex-col justify-between overflow-hidden">
        {/* Top bar: Icon on left, Value inline, Delta on right */}
        <div className="flex items-center justify-between gap-1.5 w-full min-w-0 mb-1.5 sm:mb-2.5 overflow-hidden">
          <div className="flex items-center gap-1.5 sm:gap-2.5 min-w-0 flex-1 overflow-hidden">
            <div
              style={{ width: 30, height: 30, borderRadius: 9, background: c.soft, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
              className="sm:w-[38px] sm:h-[38px]"
            >
              <div
                style={{ width: 18, height: 18, borderRadius: 5, background: c.grad, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 3px 10px ${c.glow}` }}
                className="sm:w-[24px] sm:h-[24px]"
              >
                {icon && <Icon name={icon} size={10} color="white" strokeWidth={2.2} className="sm:hidden" />}
                {icon && <Icon name={icon} size={13} color="white" strokeWidth={2.2} className="hidden sm:block" />}
              </div>
            </div>

            {/* Inline value with adaptive sizing and truncation */}
            <div className="min-w-0 flex-1 overflow-hidden">
              {loading ? (
                <div className="skeleton" style={{ height: 20, width: 60, borderRadius: 6 }} />
              ) : (
                <div
                  className={`${desktopClass} font-extrabold text-slate-800 dark:text-white truncate`}
                  style={{ fontSize: mobileFontSize, letterSpacing: "-0.02em", lineHeight: 1.2 }}
                  title={valStr}
                >
                  {value}
                </div>
              )}
            </div>
          </div>

          {/* Delta badge */}
          {delta && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                fontSize: 10,
                fontWeight: 700,
                borderRadius: 99,
                background: deltaUp ? "#d1fae5" : "#fee2e2",
                color: deltaUp ? "#065f46" : "#991b1b",
                flexShrink: 0
              }}
              className="dark:bg-opacity-20 px-1 py-0.5 sm:px-2 sm:py-1 shrink-0 ml-1"
            >
              <Icon name={deltaUp ? "trend_up" : "trend_down"} size={9} color={deltaUp ? "#065f46" : "#991b1b"} />
              <span className="hidden sm:inline" style={{ color: "inherit" }}>{delta}</span>
            </div>
          )}
        </div>

        {/* Label & Sublabel */}
        <div className="min-w-0 w-full mt-auto">
          <div
            style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em" }}
            className="text-slate-400 dark:text-slate-500 sm:text-[11px] truncate mb-0.5"
            title={label}
          >
            {label}
          </div>
          {sublabel && (
            <div
              style={{ fontSize: 10 }}
              className="text-slate-500 dark:text-slate-400 opacity-80 truncate"
              title={sublabel}
            >
              {sublabel}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
